/**
 * The governed campaign-scope handoff into Platform Analytics.
 *
 * Campaign Analysis knows which campaign it is analysing. Platform Analytics
 * knows how to report one. Between them sat nothing at all: the campaign-level
 * action opened whichever embedded platform dashboard happened to sort first,
 * so a campaign question was answered by a platform's report — TikTok, because
 * TikTok was first in the array.
 *
 * This module is that missing seam, and it is ONE implementation shared by
 * Cortex and Quantum so the two portals cannot disagree about which campaign a
 * handoff names.
 *
 * Two identifiers travel, because the two Platform Analytics surfaces resolve
 * campaigns from different catalogues:
 *   - `campaign`     — the governed media-campaign id, resolved through the
 *                      canonical campaign identity resolver. Cortex's page
 *                      lists the same records, so this matches exactly.
 *   - `campaignName` — the canonical display name, which resolves the SAME
 *                      campaign against a catalogue keyed differently.
 *
 * Id wins; the name is the fallback, never an override. Neither can introduce
 * a campaign the destination does not already list for the governed
 * advertiser — resolution only ever SELECTS from the catalogue the destination
 * fetched under its own authorization, and an unresolvable scope degrades to
 * "All campaigns" rather than inventing one.
 */

import { getCanonicalCampaignDisplayName } from "../utils/campaignDisplayName";
import { normalizeCampaignNameKey } from "../utils/canonicalCampaignIdentity";
import { resolveCampaignAnalysisReportingCampaignId } from "../utils/campaignAnalysisCampaignOptions";

export const PLATFORM_ANALYTICS_CAMPAIGN_PARAM = "campaign";
export const PLATFORM_ANALYTICS_CAMPAIGN_NAME_PARAM = "campaignName";

export const PLATFORM_ANALYTICS_CAMPAIGN_SCOPE_PARAM_KEYS = Object.freeze([
  PLATFORM_ANALYTICS_CAMPAIGN_PARAM,
  PLATFORM_ANALYTICS_CAMPAIGN_NAME_PARAM,
]);

const text = (value) => String(value ?? "").trim();

/**
 * The campaign a Campaign Analysis surface is actually reporting on.
 *
 * An explicit selection wins. Under "Auto" the governed auto-detected campaign
 * IS the analysed campaign — the one named in the selector — so it is what
 * travels. Nothing here reads an array position.
 */
export const resolvePlatformAnalyticsHandoffCampaign = ({
  selectedCampaign = null,
  autoDetectedCampaign = null,
  fallbackCampaignName = "",
} = {}) => {
  const campaign = selectedCampaign || autoDetectedCampaign || null;
  const campaignId = campaign
    ? resolveCampaignAnalysisReportingCampaignId({
        selectedCampaignId: campaign.id,
        selectedCampaign: campaign,
      })
    : "";
  /*
   * A canonical option's `name` IS the governed display name — the resolver
   * that builds the option already ranked the campaign's own name above every
   * ad-platform alias. Reaching past it to the raw record would hand off the
   * platform's name for the campaign ("DemandGen…") instead of the campaign's
   * ("Never Stop Chasing"), which no destination catalogue lists.
   */
  const campaignName =
    text(campaign?.name) ||
    getCanonicalCampaignDisplayName(campaign) ||
    text(fallbackCampaignName);
  return {
    campaignId: text(campaignId),
    campaignName: text(campaignName),
  };
};

/**
 * The destination path, scoped. The route itself is the caller's — Cortex and
 * Quantum register Platform Analytics under different paths — so this only
 * ever appends the governed scope to a route the caller already owns.
 */
export const buildPlatformAnalyticsScopePath = (
  basePath,
  { campaignId = "", campaignName = "" } = {}
) => {
  const route = text(basePath);
  if (!route) return "";
  const id = text(campaignId);
  const name = text(campaignName);
  if (!id && !name) return route;
  const query = new URLSearchParams();
  if (id) query.set(PLATFORM_ANALYTICS_CAMPAIGN_PARAM, id);
  if (name) query.set(PLATFORM_ANALYTICS_CAMPAIGN_NAME_PARAM, name);
  return `${route}?${query.toString()}`;
};

export const readPlatformAnalyticsCampaignScope = (searchParams) => {
  const read = (key) =>
    text(
      searchParams && typeof searchParams.get === "function"
        ? searchParams.get(key)
        : ""
    );
  return {
    campaignId: read(PLATFORM_ANALYTICS_CAMPAIGN_PARAM),
    campaignName: read(PLATFORM_ANALYTICS_CAMPAIGN_NAME_PARAM),
  };
};

/**
 * Resolve a carried scope against the destination's OWN campaign catalogue.
 *
 * Returns the destination's id for the campaign, or "" when the carried scope
 * names nothing the destination lists. "" is the page's existing
 * "All campaigns" value, so an unresolvable handoff degrades to the honest
 * unscoped view instead of silently selecting a different campaign.
 */
export const resolvePlatformAnalyticsCampaignScope = ({
  campaigns = [],
  campaignId = "",
  campaignName = "",
} = {}) => {
  const catalogue = Array.isArray(campaigns) ? campaigns : [];
  if (!catalogue.length) return "";

  const id = text(campaignId);
  if (id) {
    const byId = catalogue.find((campaign) => text(campaign?.id) === id);
    if (byId) return text(byId.id);
  }

  const nameKey = normalizeCampaignNameKey(campaignName);
  if (nameKey) {
    const byName = catalogue.find(
      (campaign) => normalizeCampaignNameKey(campaign?.name) === nameKey
    );
    if (byName) return text(byName.id);
  }

  return "";
};
